import React from "react"
import {
	Select,
	DatePicker,
	TimePicker,
	Input,
	Upload,
	Button,
	notification,
} from "antd"
import { Icon } from "components"
import { getStatusLabel } from "utils/status"
import Strings from "utils/strings"
import { NotDeliveredReason, ORDER_COMPLETION_STATUSES } from "../types"

/**
 * Shared building blocks for the status-change forms inside AddressDetailsModal
 * (normal change-status, bundled alternative statuses and the external override).
 * Keeping them here removes the heavy markup duplication that previously lived in
 * three places and keeps the look-and-feel consistent.
 */

const labelStyle: React.CSSProperties = {
	display: "block",
	marginBottom: 4,
	fontWeight: 500,
}

/** Neutral panel container shared by every status section. */
export const sectionPanelStyle: React.CSSProperties = {
	border: "1px solid #e8e8e8",
	backgroundColor: "#fafafa",
	borderRadius: 8,
	padding: 16,
}

export interface StatusOption {
	value: string
	label: string
}

/** The statuses an in-route order can be moved to from the normal form. */
export const getRouteStatusOptions = (): StatusOption[] => [
	{ value: "on_transit", label: Strings.orders.on_transit },
	{ value: "delivered", label: Strings.orders.delivered },
	{ value: "not_delivered", label: Strings.orders.notDelivered },
	{ value: "cancelled", label: Strings.orders.cancelled },
	{ value: "returned", label: Strings.orders.returned },
]

/** Completion statuses offered by the external last-resort override (billing excluded). */
export const getCompletionStatusOptions = (): StatusOption[] =>
	ORDER_COMPLETION_STATUSES.filter((s) => s !== "billing").map((s) => ({
		value: s,
		label: getStatusLabel(s),
	}))

export const FieldLabel: React.FC<{ required?: boolean }> = ({
	required,
	children,
}) => (
	<label style={labelStyle}>
		{children}
		{required && <span style={{ color: "red" }}> *</span>}
	</label>
)

export const StatusField: React.FC<{
	value: string
	onChange: (value: string) => void
	options: StatusOption[]
	label?: string
	required?: boolean
}> = ({ value, onChange, options, label, required = true }) => (
	<>
		<FieldLabel required={required}>
			{label ?? Strings.routes.newStatus}
		</FieldLabel>
		<Select
			style={{ width: "100%" }}
			value={value}
			onChange={onChange}
			placeholder={Strings.generic.select}
		>
			{options.map((o) => (
				<Select.Option key={o.value} value={o.value}>
					{o.label}
				</Select.Option>
			))}
		</Select>
	</>
)

export const DeliveredDateField: React.FC<{
	status: string
	value: any
	onChange: (value: any) => void
}> = ({ status, value, onChange }) => (
	<>
		<FieldLabel required={status === "delivered"}>
			{Strings.routes.deliveredDateTime}
		</FieldLabel>
		<DatePicker
			style={{ width: "100%" }}
			value={status === "delivered" ? value : null}
			onChange={onChange}
			showTime={{ format: "HH:mm" }}
			format="DD/MM/YYYY HH:mm"
			placeholder={Strings.routes.selectDateAndTime}
			disabled={status !== "delivered"}
		/>
	</>
)

export const NotDeliveredReasonField: React.FC<{
	status: string
	value: string
	reasons: NotDeliveredReason[]
	onChange: (value: string) => void
}> = ({ status, value, reasons, onChange }) => (
	<>
		<FieldLabel required={status === "not_delivered"}>
			{Strings.routes.notDeliveredReason}
		</FieldLabel>
		<Select
			style={{ width: "100%" }}
			value={value}
			onChange={onChange}
			placeholder={Strings.generic.select}
			disabled={status !== "not_delivered"}
		>
			{reasons.map((reason) => (
				<Select.Option key={reason.id} value={reason.id}>
					{reason.reason}
				</Select.Option>
			))}
		</Select>
	</>
)

export const TimeField: React.FC<{
	label: string
	value: any
	onChange: (value: any) => void
	error?: boolean
}> = ({ label, value, onChange, error }) => (
	<>
		<FieldLabel>{label}</FieldLabel>
		<TimePicker
			allowClear
			style={{ width: "100%", borderColor: error ? "#ff4d4f" : undefined }}
			value={value}
			onChange={onChange}
			format="HH:mm"
			placeholder={Strings.routes.selectTime}
		/>
	</>
)

export const NotesField: React.FC<{
	value: string
	onChange: (value: string) => void
	required?: boolean
}> = ({ value, onChange, required }) => (
	<>
		<FieldLabel required={required}>{Strings.orders.notes}</FieldLabel>
		<Input.TextArea
			rows={3}
			value={value}
			onChange={(e) => onChange(e.target.value)}
			placeholder={Strings.routes.enterNotes}
		/>
	</>
)

/**
 * Image attachments (max 2). The parent owns the file list; this component only
 * handles validation/limits and delegates add/remove back to the parent.
 */
export const AttachmentsField: React.FC<{
	fileList: any[]
	onAdd: (file: any) => void
	onRemove: (file: any) => void
}> = ({ fileList, onAdd, onRemove }) => (
	<>
		<FieldLabel>{Strings.routes.attachments}</FieldLabel>
		<Upload
			fileList={fileList}
			maxCount={2}
			accept="image/*"
			beforeUpload={(file) => {
				if (fileList.length >= 2) {
					notification.warning({
						message: Strings.routes.uploadLimit,
						description: Strings.routes.uploadLimitDescription,
						placement: "bottomRight",
					})
					return false
				}
				if (!file.type.startsWith("image/")) {
					notification.warning({
						message: Strings.routes.invalidFileType,
						description: Strings.routes.onlyImagesAllowed,
						placement: "bottomRight",
					})
					return false
				}
				onAdd(file)
				return false
			}}
			onRemove={onRemove}
		>
			<Button icon={<Icon name="upload" />} disabled={fileList.length >= 2}>
				{Strings.routes.uploadFile}
			</Button>
		</Upload>
	</>
)
